import type { AnalyticsPrerequisite, AnalyticsTask } from './analytics-client'
import type { FlushTimer } from './flush-timer'
import { settleSafely } from './transport'
import type { SendOptions } from './transport'

/** A prerequisite with the failed attempts of its call. */
interface PendingTask {
  kind: AnalyticsPrerequisite
  run: AnalyticsTask
  failures: number
}

const PRIORITY: AnalyticsPrerequisite[] = ['consent', 'attribution']

/**
 * The calls that must reach the backend before the next events batch
 * (FE-321): one pending task per kind, consent before attribution. Each keeps
 * its own retry budget, and a task in flight is awaited, never called twice.
 */
export class PrerequisiteTasks {
  private readonly pending = new Map<AnalyticsPrerequisite, PendingTask>()
  private readonly running = new Map<PendingTask, Promise<boolean>>()

  constructor(private readonly timer: FlushTimer) {}

  get size() {
    return this.pending.size
  }

  /** A later task of the same kind replaces a pending one. */
  set(kind: AnalyticsPrerequisite, run: AnalyticsTask) {
    this.pending.set(kind, { kind, run, failures: 0 })
  }

  /**
   * Runs them, highest priority first, until none is left: one may arrive
   * meanwhile. `false` while one waits for a retry.
   */
  async runAll() {
    for (let next = this.next; next; next = this.next) {
      if (!(await this.run(next, {}))) return false
    }

    return true
  }

  /** Starts every pending one at once, in priority order (a hidden page). */
  startAll(options: SendOptions) {
    return PRIORITY.flatMap(kind => {
      const task = this.pending.get(kind)
      return task ? [this.run(task, options)] : []
    })
  }

  private get next() {
    const kind = PRIORITY.find(pending => this.pending.has(pending))
    return kind ? this.pending.get(kind) : undefined
  }

  private run(task: PendingTask, options: SendOptions) {
    const running =
      this.running.get(task) ??
      this.settle(task, options).finally(() => this.running.delete(task))
    this.running.set(task, running)
    return running
  }

  /** `true` once answered or given up; `false` while a retry waits. */
  private async settle(task: PendingTask, options: SendOptions) {
    const result = await settleSafely(() => task.run(options))
    const failures = result === 'retry' ? task.failures + 1 : 0
    task.failures = failures
    if (failures && this.timer.retry(failures, task.kind)) return false

    if (this.pending.get(task.kind) === task) this.pending.delete(task.kind)
    return true
  }
}
